import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { 
    Search, 
    Shield, 
    Star, 
    Users, 
    CheckCircle, 
    ArrowRight, 
    ArrowLeft,
    MapPin,
    FileText,
    ThumbsUp
} from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Onboarding Flow Component
 * Shows first-time users how ClientCheck works
 */
const OnboardingFlow = ({ onComplete, onSkip }) => {
    const [currentStep, setCurrentStep] = useState(0);
    
    const steps = [
        {
            id: 'welcome',
            title: 'Welcome to ClientCheck',
            subtitle: 'The contractor intelligence network',
            icon: Shield,
            iconColor: 'text-primary',
            content: (
                <div className="space-y-4">
                    <p className="text-muted-foreground">
                        ClientCheck helps contractors make informed decisions about job opportunities by sharing experiences with each other.
                    </p>
                    <div className="grid grid-cols-1 gap-3 mt-6">
                        <div className="flex items-center gap-3 p-3 bg-accent/50 rounded-lg">
                            <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                            <span className="text-sm">Check contractor experiences before quoting</span>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-accent/50 rounded-lg">
                            <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                            <span className="text-sm">See payment history and job feedback</span>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-accent/50 rounded-lg">
                            <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                            <span className="text-sm">Make informed decisions about job opportunities</span>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 'search',
            title: 'Search Any Address',
            subtitle: 'Fast and simple lookup',
            icon: Search,
            iconColor: 'text-blue-500',
            content: (
                <div className="space-y-4">
                    <p className="text-muted-foreground">
                        Before quoting a job, search the property address to see if other contractors have worked there.
                    </p>
                    <div className="bg-accent/30 rounded-xl p-4 border border-border">
                        <div className="flex items-center gap-3 mb-3">
                            <MapPin className="h-5 w-5 text-primary" />
                            <div className="flex-1 bg-accent rounded-lg px-4 py-3">
                                <span className="text-muted-foreground text-sm">123 Main Street, Austin TX</span>
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground text-center">
                            Type any address to see contractor experiences
                        </p>
                    </div>
                    <div className="flex items-center gap-2 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                        <span className="text-sm text-emerald-400">Results appear instantly</span>
                    </div>
                </div>
            )
        },
        {
            id: 'score',
            title: 'Understand the Score',
            subtitle: 'Risk assessment at a glance',
            icon: Star,
            iconColor: 'text-amber-500',
            content: (
                <div className="space-y-4">
                    <p className="text-muted-foreground">
                        Each property gets a Client Reliability Score (0-100) based on contractor experiences.
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                        <div className="text-center p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                            <div className="text-2xl font-bold text-emerald-500">80-100</div>
                            <div className="text-xs text-emerald-400 font-medium mt-1">LOW RISK</div>
                        </div>
                        <div className="text-center p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                            <div className="text-2xl font-bold text-amber-500">55-79</div>
                            <div className="text-xs text-amber-400 font-medium mt-1">MODERATE</div>
                        </div>
                        <div className="text-center p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                            <div className="text-2xl font-bold text-red-500">0-54</div>
                            <div className="text-xs text-red-400 font-medium mt-1">HIGH RISK</div>
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground text-center">
                        Scores require at least 3 contractor reviews to display
                    </p>
                </div>
            )
        },
        {
            id: 'protections',
            title: 'Get Job Insights',
            subtitle: 'Actionable advice for each job',
            icon: Shield,
            iconColor: 'text-blue-500',
            content: (
                <div className="space-y-4">
                    <p className="text-muted-foreground">
                        Based on contractor feedback, ClientCheck provides insights to help you prepare for each job.
                    </p>
                    <div className="bg-accent/30 rounded-xl p-4 border border-border space-y-2">
                        <div className="flex items-center gap-3 p-2">
                            <CheckCircle className="h-4 w-4 text-emerald-500" />
                            <span className="text-sm">Payment history from past contractors</span>
                        </div>
                        <div className="flex items-center gap-3 p-2">
                            <CheckCircle className="h-4 w-4 text-amber-500" />
                            <span className="text-sm">Communication quality feedback</span>
                        </div>
                        <div className="flex items-center gap-3 p-2">
                            <CheckCircle className="h-4 w-4 text-blue-500" />
                            <span className="text-sm">Scope change frequency data</span>
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground text-center">
                        Insights are based on contractor-submitted job experiences
                    </p>
                </div>
            )
        },
        {
            id: 'contribute',
            title: 'Help Fellow Contractors',
            subtitle: 'Your reviews make a difference',
            icon: Users,
            iconColor: 'text-purple-500',
            content: (
                <div className="space-y-4">
                    <p className="text-muted-foreground">
                        After completing a job, share your experience to help other contractors make better decisions.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="p-4 bg-accent/50 rounded-lg text-center">
                            <FileText className="h-8 w-8 text-primary mx-auto mb-2" />
                            <p className="text-sm font-medium">Submit Reviews</p>
                            <p className="text-xs text-muted-foreground mt-1">Document your experience</p>
                        </div>
                        <div className="p-4 bg-accent/50 rounded-lg text-center">
                            <ThumbsUp className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                            <p className="text-sm font-medium">Earn Rank Points</p>
                            <p className="text-xs text-muted-foreground mt-1">Build your reputation</p>
                        </div>
                    </div>
                    <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                        <p className="text-sm text-center">
                            <span className="font-medium text-primary">Your reviews are anonymous</span>
                            <br />
                            <span className="text-muted-foreground text-xs">Company names are never shown to other users</span>
                        </p>
                    </div>
                </div>
            )
        }
    ];
    
    const currentStepData = steps[currentStep];
    const isLastStep = currentStep === steps.length - 1;
    const isFirstStep = currentStep === 0;
    const Icon = currentStepData.icon;
    
    const handleNext = () => {
        if (isLastStep) {
            onComplete();
        } else {
            setCurrentStep(prev => prev + 1);
        }
    };
    
    const handleBack = () => {
        if (!isFirstStep) {
            setCurrentStep(prev => prev - 1);
        }
    };
    
    return (
        <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md bg-card border-border shadow-2xl" data-testid="onboarding-modal">
                <CardContent className="p-6">
                    {/* Progress Dots */}
                    <div className="flex justify-center gap-2 mb-6">
                        {steps.map((_, index) => (
                            <div 
                                key={index}
                                className={cn(
                                    "h-2 rounded-full transition-all duration-300",
                                    index === currentStep 
                                        ? "w-6 bg-primary" 
                                        : index < currentStep 
                                            ? "w-2 bg-primary/50" 
                                            : "w-2 bg-muted"
                                )}
                            />
                        ))}
                    </div>
                    
                    {/* Icon */}
                    <div className="flex justify-center mb-4">
                        <div className={cn(
                            "p-4 rounded-2xl",
                            currentStepData.iconColor === 'text-primary' ? 'bg-primary/10' :
                            currentStepData.iconColor === 'text-blue-500' ? 'bg-blue-500/10' :
                            currentStepData.iconColor === 'text-amber-500' ? 'bg-amber-500/10' :
                            currentStepData.iconColor === 'text-purple-500' ? 'bg-purple-500/10' :
                            'bg-accent'
                        )}>
                            <Icon className={cn("h-8 w-8", currentStepData.iconColor)} />
                        </div>
                    </div>
                    
                    {/* Title */}
                    <div className="text-center mb-6">
                        <h2 className="text-xl font-bold text-foreground mb-1">
                            {currentStepData.title}
                        </h2>
                        <p className="text-sm text-muted-foreground">
                            {currentStepData.subtitle}
                        </p>
                    </div>
                    
                    {/* Content */}
                    <div className="mb-6">
                        {currentStepData.content}
                    </div>
                    
                    {/* Navigation */}
                    <div className="flex items-center justify-between">
                        {!isFirstStep ? (
                            <Button 
                                variant="ghost" 
                                onClick={handleBack}
                                className="gap-2"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Back
                            </Button>
                        ) : (
                            <Button 
                                variant="ghost" 
                                onClick={onSkip}
                                className="text-muted-foreground"
                            >
                                Skip
                            </Button>
                        )}
                        
                        <Button 
                            onClick={handleNext}
                            className="gap-2"
                            data-testid="onboarding-next-btn"
                        >
                            {isLastStep ? 'Get Started' : 'Next'}
                            {!isLastStep && <ArrowRight className="h-4 w-4" />}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default OnboardingFlow;
